import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'react-oidc-context';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Flag, Image as ImageIcon, MagnifyingGlass, Trash, X } from '@phosphor-icons/react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { OpponentAvatar } from '@/components/schedule/OpponentAvatar';
import { useConfirm } from '@/components/ConfirmDialog';
import {
  directoryLogoUrl,
  recordResult,
  reportOpponentLogo,
  searchOpponents,
  type Fixture,
  type MatchDetailsValue,
  type OpponentResult,
} from '@/api/fixtures';

const DEBOUNCE_MS = 250;

// MatchDetailsSection is the "game UI" the schedule forms reveal when an
// activity's type is `match`. It's a controlled editor over MatchDetailsValue:
// an opponent combobox (free-text + cross-org club directory with logos), a
// home/away toggle, an optional competition field, and a logo control. The
// parent owns persistence (create: after the activity exists; edit: on save)
// via persistMatchDetails — this component only collects intent.
export function MatchDetailsSection({
  slug,
  value,
  onChange,
}: {
  slug: string;
  value: MatchDetailsValue;
  onChange: (next: MatchDetailsValue) => void;
}) {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const token = user?.access_token ?? null;
  const confirm = useConfirm();

  const [query, setQuery] = useState(value.opponentName);
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const blurTimer = useRef<number | undefined>(undefined);

  // Reporting a picked community club's crest (wrong / offensive) — the safety
  // valve for the shared, crowd-sourced directory.
  const reportMut = useMutation({
    mutationFn: () =>
      reportOpponentLogo(token, slug, value.communityClubId, 'wrong_logo'),
  });
  async function handleReportLogo() {
    if (!value.communityClubId) return;
    const ok = await confirm({
      title: t('schedule.match.opponent.reportTitle'),
      body: t('schedule.match.opponent.reportBody'),
      confirmLabel: t('schedule.match.opponent.reportCta'),
    });
    if (ok) reportMut.mutate();
  }

  // The opponent input is the combobox: typing edits the free-text name AND
  // drives the search. Keep the local `query` in sync when the parent resets
  // the value (e.g. the form re-seeds on a different activity).
  useEffect(() => {
    setQuery(value.opponentName);
  }, [value.opponentName]);

  useEffect(() => {
    const id = window.setTimeout(() => setDebounced(query.trim()), DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [query]);

  const searchQuery = useQuery({
    queryKey: ['opponents', slug, debounced],
    queryFn: () => searchOpponents(token, slug, debounced),
    enabled: open && debounced.length >= 2 && !!slug,
  });
  const results = searchQuery.data ?? [];

  const hasDirectoryMatch =
    value.orgSlug !== '' || value.legacyClubId !== null || value.communityClubId !== '';

  function selectResult(r: OpponentResult) {
    if (blurTimer.current) window.clearTimeout(blurTimer.current);
    reportMut.reset();
    onChange({
      ...value,
      opponentName: r.name,
      orgSlug: r.source === 'go' ? r.slug ?? '' : '',
      legacyClubId: r.source === 'legacy' ? r.legacy_club_id ?? null : null,
      communityClubId: r.source === 'community' ? r.community_club_id ?? '' : '',
      // A directory pick supplies the logo — drop any pending manual crest.
      logoFile: null,
      clearLogo: false,
      logoPreviewUrl: directoryLogoUrl(r),
    });
    setQuery(r.name);
    setOpen(false);
  }

  function onTypeName(next: string) {
    setQuery(next);
    setOpen(true);
    // Editing the name detaches any directory match — it's now free text. Keep
    // a manually-uploaded crest (logoFile) intact; only clear directory refs.
    onChange({
      ...value,
      opponentName: next,
      orgSlug: '',
      legacyClubId: null,
      communityClubId: '',
      logoPreviewUrl: value.logoFile ? value.logoPreviewUrl : hasDirectoryMatch ? null : value.logoPreviewUrl,
    });
  }

  return (
    <section className="rounded-2xl border border-border bg-muted/30 p-4">
      <h3 className="font-heading text-sm font-bold text-foreground">
        {t('schedule.match.section')}
      </h3>

      <div className="mt-3 flex flex-col gap-4">
        {/* Opponent combobox */}
        <label className="block">
          <span className="font-heading text-sm font-semibold">
            {t('schedule.match.opponent.label')}
          </span>
          <div className="relative mt-1">
            <div className="relative">
              {value.logoPreviewUrl ? (
                <span className="pointer-events-none absolute left-2 top-1/2 -translate-y-1/2">
                  <OpponentAvatar src={value.logoPreviewUrl} name={value.opponentName} size={24} />
                </span>
              ) : (
                <MagnifyingGlass
                  className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                  weight="bold"
                  aria-hidden
                />
              )}
              <Input
                value={query}
                placeholder={t('schedule.match.opponent.placeholder')}
                className="pl-9 pr-9"
                autoComplete="off"
                onChange={(e) => onTypeName(e.target.value)}
                onFocus={() => setOpen(true)}
                onBlur={() => {
                  // Delay so a click on a result registers before we close.
                  blurTimer.current = window.setTimeout(() => setOpen(false), 150);
                }}
              />
              {value.opponentName ? (
                <button
                  type="button"
                  aria-label={t('schedule.match.opponent.clear')}
                  className="absolute right-2 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-pill text-muted-foreground hover:bg-muted hover:text-foreground"
                  onClick={() => {
                    setQuery('');
                    setOpen(false);
                    reportMut.reset();
                    onChange({
                      ...value,
                      opponentName: '',
                      orgSlug: '',
                      legacyClubId: null,
                      communityClubId: '',
                      logoFile: null,
                      // Removing the opponent on an existing fixture should also
                      // drop a stored crest on save.
                      clearLogo: !value.logoFile && value.logoPreviewUrl != null,
                      logoPreviewUrl: null,
                    });
                  }}
                >
                  <X weight="bold" className="h-4 w-4" />
                </button>
              ) : null}
            </div>

            {open && debounced.length >= 2 ? (
              <ul className="absolute z-10 mt-1 max-h-64 w-full overflow-y-auto rounded-xl border border-border bg-card py-1 shadow-lg">
                {searchQuery.isLoading ? (
                  <li className="px-3 py-2 text-sm text-muted-foreground">
                    {t('schedule.match.opponent.searching')}
                  </li>
                ) : results.length === 0 ? (
                  <li className="px-3 py-2 text-sm text-muted-foreground">
                    {t('schedule.match.opponent.noResults')}
                  </li>
                ) : (
                  results.map((r) => (
                    <li key={`${r.source}-${r.slug ?? r.legacy_club_id ?? r.name}`}>
                      <button
                        type="button"
                        className="flex w-full items-center gap-3 px-3 py-2 text-left hover:bg-muted"
                        // onMouseDown beats the input's onBlur, so the pick lands.
                        onMouseDown={(e) => {
                          e.preventDefault();
                          selectResult(r);
                        }}
                      >
                        <OpponentAvatar src={directoryLogoUrl(r)} name={r.name} size={28} />
                        <span className="flex min-w-0 flex-col">
                          <span className="truncate font-heading text-sm font-semibold">
                            {r.name}
                          </span>
                          {r.municipality || r.sport ? (
                            <span className="truncate text-xs text-muted-foreground">
                              {[r.municipality, r.sport].filter(Boolean).join(' · ')}
                            </span>
                          ) : null}
                        </span>
                      </button>
                    </li>
                  ))
                )}
              </ul>
            ) : null}
          </div>
          {hasDirectoryMatch ? (
            <div className="mt-1 flex items-center gap-2">
              <p className="text-xs text-muted-foreground">
                {value.communityClubId
                  ? t('schedule.match.opponent.fromCommunity')
                  : t('schedule.match.opponent.fromDirectory')}
              </p>
              {value.communityClubId ? (
                reportMut.isSuccess ? (
                  <span className="text-xs text-muted-foreground">
                    {t('schedule.match.opponent.reported')}
                  </span>
                ) : (
                  <button
                    type="button"
                    aria-label={t('schedule.match.opponent.reportCta')}
                    title={t('schedule.match.opponent.reportCta')}
                    disabled={reportMut.isPending}
                    className="flex h-6 w-6 items-center justify-center rounded-pill text-muted-foreground hover:bg-muted hover:text-foreground disabled:opacity-50"
                    onClick={handleReportLogo}
                  >
                    <Flag weight="bold" className="h-4 w-4" />
                  </button>
                )
              ) : null}
            </div>
          ) : null}
        </label>

        {/* Home / away */}
        <div>
          <span className="font-heading text-sm font-semibold">
            {t('schedule.match.homeAway.label')}
          </span>
          <div className="mt-1 inline-flex rounded-pill border border-border bg-background p-0.5">
            <ToggleButton
              active={value.isHome}
              label={t('schedule.match.homeAway.home')}
              onClick={() => onChange({ ...value, isHome: true })}
            />
            <ToggleButton
              active={!value.isHome}
              label={t('schedule.match.homeAway.away')}
              onClick={() => onChange({ ...value, isHome: false })}
            />
          </div>
        </div>

        {/* Competition */}
        <label className="block">
          <span className="font-heading text-sm font-semibold">
            {t('schedule.match.competition.label')}{' '}
            <span className="font-normal text-muted-foreground">
              {t('schedule.match.competition.optional')}
            </span>
          </span>
          <Input
            className="mt-1"
            value={value.competitionName}
            placeholder={t('schedule.match.competition.placeholder')}
            onChange={(e) => onChange({ ...value, competitionName: e.target.value })}
          />
        </label>

        {/* Logo */}
        <LogoControl value={value} onChange={onChange} disabled={hasDirectoryMatch} />
      </div>
    </section>
  );
}

function ToggleButton({
  active,
  label,
  onClick,
}: {
  active: boolean;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`rounded-pill px-4 py-1.5 text-sm font-semibold transition-colors ${
        active
          ? 'bg-primary text-primary-foreground'
          : 'text-foreground/70 hover:text-foreground'
      }`}
    >
      {label}
    </button>
  );
}

// LogoControl manages the manual crest: a preview (file object URL, stored
// logo, or directory logo) plus upload / replace / remove. It's disabled when a
// directory match owns the logo — the club's crest is shown read-only there.
function LogoControl({
  value,
  onChange,
  disabled,
}: {
  value: MatchDetailsValue;
  onChange: (next: MatchDetailsValue) => void;
  disabled: boolean;
}) {
  const { t } = useTranslation('common');
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  // Track the object URL we created so we can revoke it (avoid leaks) when the
  // file changes or the component unmounts.
  const objectUrlRef = useRef<string | null>(null);

  useEffect(() => {
    return () => {
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    };
  }, []);

  function pickFile(file: File) {
    if (!file.type.startsWith('image/')) {
      setError(t('schedule.match.logo.notImage'));
      return;
    }
    setError(null);
    if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
    const url = URL.createObjectURL(file);
    objectUrlRef.current = url;
    onChange({
      ...value,
      logoFile: file,
      clearLogo: false,
      orgSlug: '',
      legacyClubId: null,
      communityClubId: '',
      logoPreviewUrl: url,
    });
  }

  function removeLogo() {
    if (objectUrlRef.current) {
      URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = null;
    }
    onChange({
      ...value,
      logoFile: null,
      // If a crest was already stored (no pending file), mark it for deletion.
      clearLogo: !value.logoFile && value.logoPreviewUrl != null,
      logoPreviewUrl: null,
    });
  }

  return (
    <div>
      <span className="font-heading text-sm font-semibold">
        {t('schedule.match.logo.label')}
      </span>
      <div className="mt-1 flex items-center gap-3">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-border bg-background">
          {value.logoPreviewUrl ? (
            <OpponentAvatar src={value.logoPreviewUrl} name={value.opponentName} size={56} square />
          ) : (
            <ImageIcon className="h-6 w-6 text-muted-foreground" weight="duotone" aria-hidden />
          )}
        </div>
        {disabled ? (
          <p className="text-xs text-muted-foreground">
            {t('schedule.match.logo.fromClub', { name: value.opponentName })}
          </p>
        ) : (
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => inputRef.current?.click()}
              >
                {value.logoPreviewUrl
                  ? t('schedule.match.logo.replace')
                  : t('schedule.match.logo.upload')}
              </Button>
              {value.logoPreviewUrl ? (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="text-destructive"
                  onClick={removeLogo}
                >
                  <Trash weight="bold" className="h-4 w-4" />
                  {t('schedule.match.logo.remove')}
                </Button>
              ) : null}
            </div>
            <p className="text-xs text-muted-foreground">{t('schedule.match.logo.hint')}</p>
            <p className="text-xs text-muted-foreground">{t('schedule.match.logo.sharedNote')}</p>
          </div>
        )}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) pickFile(file);
          e.target.value = '';
        }}
      />
      {error ? <p className="mt-1 text-xs text-destructive">{error}</p> : null}
    </div>
  );
}

// MatchResultSection records the final score for an existing match activity.
// Edit-only — there's no fixture to score until the activity exists. Recording
// a score flips result_status to 'final' server-side. Self-contained: owns its
// recordResult mutation and reports success up via onRecorded.
export function MatchResultSection({
  slug,
  activityId,
  fixture,
  onRecorded,
}: {
  slug: string;
  activityId: string;
  fixture: Fixture;
  onRecorded: () => void;
}) {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const token = user?.access_token ?? null;

  const [home, setHome] = useState<string>(
    fixture.home_score != null ? String(fixture.home_score) : '',
  );
  const [away, setAway] = useState<string>(
    fixture.away_score != null ? String(fixture.away_score) : '',
  );

  const recordMut = useMutation({
    mutationFn: () =>
      recordResult(token, slug, activityId, Number(home), Number(away)),
    onSuccess: onRecorded,
  });

  const valid = useMemo(() => {
    const h = Number(home);
    const a = Number(away);
    return (
      home !== '' &&
      away !== '' &&
      Number.isInteger(h) &&
      Number.isInteger(a) &&
      h >= 0 &&
      a >= 0
    );
  }, [home, away]);

  const isFinal = fixture.result_status === 'final';

  return (
    <section className="rounded-2xl border border-border bg-muted/30 p-4">
      <h3 className="font-heading text-sm font-bold text-foreground">
        {t('schedule.match.result.section')}
      </h3>
      <div className="mt-3 flex items-end gap-3">
        <ScoreField
          label={t('schedule.match.result.home')}
          value={home}
          onChange={setHome}
        />
        <span className="pb-2 text-lg font-bold text-muted-foreground">–</span>
        <ScoreField
          label={t('schedule.match.result.away')}
          value={away}
          onChange={setAway}
        />
        <Button
          type="button"
          className="ml-auto"
          disabled={!valid || recordMut.isPending}
          onClick={() => recordMut.mutate()}
        >
          {recordMut.isPending
            ? t('schedule.match.result.saving')
            : isFinal
              ? t('schedule.match.result.update')
              : t('schedule.match.result.record')}
        </Button>
      </div>
      {recordMut.isError ? (
        <p role="alert" className="mt-2 text-xs text-destructive">
          {recordMut.error instanceof Error
            ? recordMut.error.message
            : t('schedule.match.result.error')}
        </p>
      ) : (
        <p className="mt-2 text-xs text-muted-foreground">
          {t('schedule.match.result.hint')}
        </p>
      )}
    </section>
  );
}

function ScoreField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-semibold text-muted-foreground">{label}</span>
      <Input
        inputMode="numeric"
        pattern="[0-9]*"
        className="w-16 text-center"
        value={value}
        onChange={(e) => onChange(e.target.value.replace(/[^0-9]/g, ''))}
      />
    </label>
  );
}
